import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
export const getInitials = (name = "") => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return " ";
  const first = words[0]?.[0] || "";
  const second = words[1]?.[0] || "";
  const pair = (first + second).toUpperCase();
  if (pair.length >= 2) return pair.slice(0, 2);
  return (words[0].slice(0, 2) || "").toUpperCase();
};

export const Avatar = ({ name, colorClass = "bg-chilli", children }) => (
  <div
    className={[
      "flex-none shrink-0",
      "w-9 sm:w-10 aspect-square rounded-full",
      "flex items-center justify-center select-none leading-none",
      "text-sm sm:text-base font-oswald text-white",
      colorClass || "bg-chilli",
    ].join(" ")}
  >
    {getInitials(name) || "  "}
    {children}
  </div>
);


export const BottomSheet = ({ open, onClose, title = "Switch venues", children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={[
        "fixed inset-0 z-[100000]",
        open ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
      aria-hidden={!open}
    >
      <div
        className={[
          "absolute inset-0 bg-black/60 transition-opacity",
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
        onClick={onClose}
      />

      <div className="absolute inset-x-0 bottom-0">
        <div className="mx-auto w-full md:max-w-[600px] sm:px-0">
          <div
            role="dialog"
            aria-modal="true"
            className={[
              " rounded-t-[28px] shadow-xl",
              "transition-transform duration-300",
              open ? "translate-y-0 bg-white" : "translate-y-full bg-black",
            ].join(" ")}
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="py-2">
              <div className="w-16 h-1.5 rounded-full mx-auto my-2" />
            </div>

            <div className="px-6 pb-4">
              <h3 className="text-xl md:text-2xl font-semibold text-black mb-4">
                {title}
              </h3>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const CompanySelectInput = ({
  dataModel,
  options,
  label,
  readOnly,
  onChange,
  showButton,
  buttonText,
  onButtonClick,
  companyIdFieldName,
  customClassName,
  title,
  sideIcon,
  showAvatar,
}) => {
  companyIdFieldName ||= "companyId";
  dataModel ||= {};
  options ||= [];
  readOnly ||= options.length <= 1;
  label ||= options.length === 1 ? "Venue" : "Select a venue";
  customClassName ||= "";
  title ||= "Switch venues";
  sideIcon ||= <SwapVertIcon className="text-white text-2xl" />;
  showAvatar = showAvatar === undefined ? true : showAvatar;

  const selectedId = dataModel[companyIdFieldName] ?? options[0]?.id ?? null;
  const selected = useMemo(
    () =>
      options.find((o) => String(o.id) === String(selectedId)) || options[0],
    [options, selectedId]
  );

  const [open, setOpen] = useState(false);

  const handlePick = (id) => {
    onChange && onChange(id);
    setOpen(false);
  };

  const onButtonClickHandler = () => {
    onButtonClick && onButtonClick(selectedId);
  };

  return (
    <div className="">

      <button
        type="button"
        disabled={readOnly}
        onClick={() => !readOnly && setOpen(true)}
        className={[
          "w-full h-[60px] sm:h-[68px] md:h-[72px] px-3 sm:px-4",
          "bg-background-secondary rounded-md",
          "ring-1 ring-[#34353F] text-white",
          "flex items-center justify-between overflow-hidden",
          readOnly ? "opacity-70 cursor-default" : " ",
          `${customClassName}`,
        ].join(" ")}
      >
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
          {showAvatar && <Avatar
            name={selected?.name}
            colorClass={selected?.color || "bg-chilli"}
          />}
          <div
            className="font-headings-primary text-base leading-tight tracking-wide text-white truncate"
            title={selected?.name}
          >
            {selected?.name || "Venue"}
          </div>
        </div>
        {sideIcon}
      </button>

      {showButton ? (
        <button
          type="button"
          className="btn btn-primary mt-3"
          onClick={onButtonClickHandler}
        >
          {buttonText}
        </button>
      ) : null}

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={title}
      >
        <div className="space-y-3 max-h-[40vh] overflow-auto">
          {options.map((o) => {
            const isActive = String(o.id) === String(selectedId);
            return (
              <button
                type="button"
                key={o.id}
                disabled={readOnly}
                onClick={() => handlePick(o.id)}
                className={[
                  "w-full flex items-center justify-between",
                  "rounded-2xl px-3 py-2.5 sm:py-3",
                  isActive ? "bg-neutral-100" : "hover:bg-black/5",
                ].join(" ")}
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <Avatar name={o.name} colorClass={o.color || "bg-chilli"} />
                  <div
                    className="text-base font-semibold text-black truncate"
                    title={o.name}
                  >
                    {o.name}
                  </div>
                </div>

                {isActive ? (
                  <i
                    className="bi bi-check-lg text-chilli text-lg sm:text-xl shrink-0 ml-2"
                    aria-label="Selected"
                  />
                ) : (
                  <span className="shrink-0 w-5" />
                )}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </div>
  );
};


export const CompanySelectUserForm = ({
  dataModel,
  options,
  label,
  readOnly,
  onChange,
  showButton,
  buttonText,
  onButtonClick,
  companyIdFieldName,
  customClassName,
  title,
  sideIcon,
  showAvatar,
}) => {
  companyIdFieldName ||= "companyId";
  dataModel ||= {};
  options ||= [];
  readOnly ||= options.length <= 1;
  label ||= options.length === 1 ? "Venue" : "Select a venue";
  customClassName ||= "";
  title ||= "Switch venues";
  sideIcon ||= <SwapVertIcon className="text-white text-2xl" />;
  showAvatar = showAvatar === undefined ? true : showAvatar;

  const selectedId = dataModel[companyIdFieldName] ?? options[0]?.id ?? null;
  const selected = useMemo(
    () =>
      options.find((o) => String(o.id) === String(selectedId)) || options[0],
    [options, selectedId]
  );

  const [open, setOpen] = useState(false);

  const handlePick = (id) => {
    onChange && onChange(id);
    setOpen(false);
  };

  const onButtonClickHandler = () => {
    onButtonClick && onButtonClick(selectedId);
  };

  return (
    <div className="">

      <button
        type="button"
        disabled={readOnly}
        onClick={() => !readOnly && setOpen(true)}
        className={[
          "w-full h-[60px] sm:h-[60px] md:h-[72px] ",
          "bg-background-secondary rounded-md",
          "ring-1 ring-[#34353F] text-white",
          "flex items-center justify-between overflow-hidden",
          readOnly ? "opacity-70 cursor-default" : "hover:bg-white/5 ",
          `${customClassName}`,
        ].join(" ")}
      >
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
          {showAvatar && <Avatar
            name={selected?.name}
            colorClass={selected?.color || "bg-chilli"}
          />}
          <div
            className="font-body-primary  tracking-wide text-white truncate"
            title={selected?.name}
          >
            {selected?.name || "Venue"}
          </div>
        </div>
        {sideIcon}
      </button>

      {showButton ? (
        <button
          type="button"
          className="btn btn-primary mt-3"
          onClick={onButtonClickHandler}
        >
          {buttonText}
        </button>
      ) : null}

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={title}
      >
        <div className="space-y-3 max-h-[40vh] overflow-auto">
          {options.map((o) => {
            const isActive = String(o.id) === String(selectedId);
            return (
              <button
                type="button"
                key={o.id}
                disabled={readOnly}
                onClick={() => handlePick(o.id)}
                className={[
                  "w-full flex items-center justify-between",
                  "rounded-2xl px-3 py-2.5 sm:py-3",
                  isActive ? "bg-neutral-100" : "hover:bg-black/5",
                ].join(" ")}
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <Avatar name={o.name} colorClass={o.color || "bg-chilli"} />
                  <div
                    className="text-base font-semibold text-black truncate"
                    title={o.name}
                  >
                    {o.name}
                  </div>
                </div>

                {isActive ? (
                  <i
                    className="bi bi-check-lg text-chilli text-lg sm:text-xl shrink-0 ml-2"
                    aria-label="Selected"
                  />
                ) : (
                  <span className="shrink-0 w-5" />
                )}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </div>
  );
};
